import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { AcademicCapIcon, BookOpenIcon, UserGroupIcon, CogIcon } from '@heroicons/react/24/outline';

const YesPartialNoRadio = ({ name, value, onChange, className = '' }) => {
  const options = [
    { value: 'yes', label: 'Yes', color: 'text-green-600' },
    { value: 'partial', label: 'Partial', color: 'text-yellow-600' },
    { value: 'no', label: 'No', color: 'text-red-600' },
  ];

  return (
    <div className={`flex gap-4 ${className}`}>
      {options.map(option => (
        <label key={option.value} className="flex cursor-pointer items-center gap-2">
          <input
            type="radio"
            name={name}
            value={option.value}
            checked={value === option.value}
            onChange={e => onChange(e.target.value)}
            className="h-4 w-4 border-gray-300 focus:ring-2 focus:ring-blue-500"
          />
          <span className={`text-sm font-medium ${option.color}`}>{option.label}</span>
        </label>
      ))}
    </div>
  );
};

const CurriculumDeliveryAssessment = ({ data = {}, onSave, userRole }) => {
  const [assessmentData, setAssessmentData] = useState({
    trainingMethodology: {
      modular: data.trainingMethodology?.modular || 'no',
      selfPaced: data.trainingMethodology?.selfPaced || 'no',
      instructorLed: data.trainingMethodology?.instructorLed || 'no',
      skillsFramework: data.trainingMethodology?.skillsFramework || 'no',
      notes: data.trainingMethodology?.notes || '',
    },
    contentRelevance: {
      businessDesign: data.contentRelevance?.businessDesign || 'no',
      valueProposition: data.contentRelevance?.valueProposition || 'no',
      customerJourney: data.contentRelevance?.customerJourney || 'no',
      revenueModeling: data.contentRelevance?.revenueModeling || 'no',
      goToMarket: data.contentRelevance?.goToMarket || 'no',
      notes: data.contentRelevance?.notes || '',
    },
    mentorshipCoaching: {
      coachesAllocated: data.mentorshipCoaching?.coachesAllocated || 'no',
      monthlyCheckins: data.mentorshipCoaching?.monthlyCheckins || 'no',
      reviewsEmbedded: data.mentorshipCoaching?.reviewsEmbedded || 'no',
      structuredSupport: data.mentorshipCoaching?.structuredSupport || 'no',
      notes: data.mentorshipCoaching?.notes || '',
    },
    capacityBuilding: {
      trainTheTrainer: data.capacityBuilding?.trainTheTrainer || 'no',
      officerCoaching: data.capacityBuilding?.officerCoaching || 'no',
      performanceReview: data.capacityBuilding?.performanceReview || 'no',
      continuousLearning: data.capacityBuilding?.continuousLearning || 'no',
      notes: data.capacityBuilding?.notes || '',
    },
    score: data.score || 0,
    notes: data.notes || '',
  });

  const [saving, setSaving] = useState(false);

  const calculateScore = () => {
    let totalPoints = 0;
    let maxPoints = 0;

    const getPoints = value => {
      if (value === 'yes') return 1;
      if (value === 'partial') return 0.5;
      return 0;
    };

    const trainingMethodology = assessmentData.trainingMethodology;
    totalPoints += getPoints(trainingMethodology.modular);
    totalPoints += getPoints(trainingMethodology.selfPaced);
    totalPoints += getPoints(trainingMethodology.instructorLed);
    totalPoints += getPoints(trainingMethodology.skillsFramework);
    maxPoints += 4;

    const contentRelevance = assessmentData.contentRelevance;
    totalPoints += getPoints(contentRelevance.businessDesign);
    totalPoints += getPoints(contentRelevance.valueProposition);
    totalPoints += getPoints(contentRelevance.customerJourney);
    totalPoints += getPoints(contentRelevance.revenueModeling);
    totalPoints += getPoints(contentRelevance.goToMarket);
    maxPoints += 5;

    const mentorshipCoaching = assessmentData.mentorshipCoaching;
    totalPoints += getPoints(mentorshipCoaching.coachesAllocated);
    totalPoints += getPoints(mentorshipCoaching.monthlyCheckins);
    totalPoints += getPoints(mentorshipCoaching.reviewsEmbedded);
    totalPoints += getPoints(mentorshipCoaching.structuredSupport);
    maxPoints += 4;

    const capacityBuilding = assessmentData.capacityBuilding;
    totalPoints += getPoints(capacityBuilding.trainTheTrainer);
    totalPoints += getPoints(capacityBuilding.officerCoaching);
    totalPoints += getPoints(capacityBuilding.performanceReview);
    totalPoints += getPoints(capacityBuilding.continuousLearning);
    maxPoints += 4;

    return Math.round((totalPoints / maxPoints) * 100);
  };

  const handleSave = async () => {
    setSaving(true);
    const updatedData = {
      ...assessmentData,
      score: calculateScore(),
      lastUpdated: new Date().toISOString(),
    };

    try {
      await onSave(updatedData);
    } finally {
      setSaving(false);
    }
  };

  const updateField = (section, field, value) => {
    setAssessmentData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  const getScoreColor = score => {
    if (score >= 81) return 'text-green-600';
    if (score >= 61) return 'text-yellow-600';
    if (score >= 41) return 'text-orange-600';
    return 'text-red-600';
  };

  const getScoreStatus = score => {
    if (score >= 81) return 'Healthy & Scalable';
    if (score >= 61) return 'Emerging Maturity';
    if (score >= 41) return 'At Risk';
    return 'Critical Gaps';
  };

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-purple-200 bg-purple-50 p-6">
        <div className="mb-4 flex items-center gap-3">
          <AcademicCapIcon className="h-8 w-8 text-purple-600" />
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Curriculum & Program Delivery</h2>
            <p className="text-gray-600">
              Assess the quality and effectiveness of training methodology, content, and delivery
              mechanisms
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm text-gray-600">Current Score</div>
            <div className={`text-3xl font-bold ${getScoreColor(calculateScore())}`}>
              {calculateScore()}%
            </div>
            <div className="text-sm text-gray-600">{getScoreStatus(calculateScore())}</div>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="rounded-lg bg-purple-600 px-6 py-3 font-medium text-white transition-colors hover:bg-purple-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Assessment'}
          </button>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm"
      >
        <div className="mb-4 flex items-center gap-2">
          <BookOpenIcon className="h-6 w-6 text-blue-600" />
          <h3 className="text-xl font-semibold text-gray-900">Training Methodology</h3>
        </div>

        <div className="space-y-6">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">Is the training modular?</label>
            <p className="text-xs text-gray-600">
              Content is organized into discrete, manageable modules that can be completed
              independently
            </p>
            <YesPartialNoRadio
              name="modular"
              value={assessmentData.trainingMethodology.modular}
              onChange={value => updateField('trainingMethodology', 'modular', value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">Is the training self-paced?</label>
            <p className="text-xs text-gray-600">
              Participants can progress through content at their own speed and schedule
            </p>
            <YesPartialNoRadio
              name="selfPaced"
              value={assessmentData.trainingMethodology.selfPaced}
              onChange={value => updateField('trainingMethodology', 'selfPaced', value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Is the training instructor-led?
            </label>
            <p className="text-xs text-gray-600">
              Qualified instructors facilitate learning sessions and provide guidance
            </p>
            <YesPartialNoRadio
              name="instructorLed"
              value={assessmentData.trainingMethodology.instructorLed}
              onChange={value => updateField('trainingMethodology', 'instructorLed', value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Does it follow a skills development framework aligned with business growth stages?
            </label>
            <p className="text-xs text-gray-600">
              Training progression matches the development stages of entrepreneurs and their
              businesses
            </p>
            <YesPartialNoRadio
              name="skillsFramework"
              value={assessmentData.trainingMethodology.skillsFramework}
              onChange={value => updateField('trainingMethodology', 'skillsFramework', value)}
            />
          </div>

          <div>
            <label
              htmlFor="trainingMethodologyNotes"
              className="mb-2 block text-sm font-medium text-gray-700"
            >
              Additional Notes
            </label>
            <textarea
              id="trainingMethodologyNotes"
              value={assessmentData.trainingMethodology.notes}
              onChange={e => updateField('trainingMethodology', 'notes', e.target.value)}
              rows={3}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
              placeholder="Add any additional observations or comments..."
            />
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm"
      >
        <div className="mb-4 flex items-center gap-2">
          <AcademicCapIcon className="h-6 w-6 text-green-600" />
          <h3 className="text-xl font-semibold text-gray-900">Content Relevance</h3>
        </div>

        <div className="space-y-6">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Does the curriculum cover business design elements?
            </label>
            <p className="text-xs text-gray-600">
              Content includes business model development, value creation, and strategic planning
            </p>
            <YesPartialNoRadio
              name="businessDesign"
              value={assessmentData.contentRelevance.businessDesign}
              onChange={value => updateField('contentRelevance', 'businessDesign', value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Does the curriculum cover value proposition design?
            </label>
            <p className="text-xs text-gray-600">
              Content includes how to identify, articulate, and communicate value to customers
            </p>
            <YesPartialNoRadio
              name="valueProposition"
              value={assessmentData.contentRelevance.valueProposition}
              onChange={value => updateField('contentRelevance', 'valueProposition', value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Does the curriculum cover customer journey mapping?
            </label>
            <p className="text-xs text-gray-600">
              Content includes understanding customer experiences and touchpoints
            </p>
            <YesPartialNoRadio
              name="customerJourney"
              value={assessmentData.contentRelevance.customerJourney}
              onChange={value => updateField('contentRelevance', 'customerJourney', value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Does the curriculum cover revenue modeling?
            </label>
            <p className="text-xs text-gray-600">
              Content includes pricing strategies, revenue streams, and financial sustainability
            </p>
            <YesPartialNoRadio
              name="revenueModeling"
              value={assessmentData.contentRelevance.revenueModeling}
              onChange={value => updateField('contentRelevance', 'revenueModeling', value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Does the curriculum cover go-to-market strategies?
            </label>
            <p className="text-xs text-gray-600">
              Content includes market entry strategies, customer acquisition, and scaling approaches
            </p>
            <YesPartialNoRadio
              name="goToMarket"
              value={assessmentData.contentRelevance.goToMarket}
              onChange={value => updateField('contentRelevance', 'goToMarket', value)}
            />
          </div>

          <div>
            <label
              htmlFor="contentRelevanceNotes"
              className="mb-2 block text-sm font-medium text-gray-700"
            >
              Additional Notes
            </label>
            <textarea
              id="contentRelevanceNotes"
              value={assessmentData.contentRelevance.notes}
              onChange={e => updateField('contentRelevance', 'notes', e.target.value)}
              rows={3}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-green-500 focus:ring-green-500"
              placeholder="Add any additional observations or comments..."
            />
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm"
      >
        <div className="mb-4 flex items-center gap-2">
          <UserGroupIcon className="h-6 w-6 text-orange-600" />
          <h3 className="text-xl font-semibold text-gray-900">Mentorship & Coaching</h3>
        </div>

        <div className="space-y-6">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Are coaches or mentors allocated per client?
            </label>
            <p className="text-xs text-gray-600">
              Each entrepreneur has access to dedicated coaching or mentoring support
            </p>
            <YesPartialNoRadio
              name="coachesAllocated"
              value={assessmentData.mentorshipCoaching.coachesAllocated}
              onChange={value => updateField('mentorshipCoaching', 'coachesAllocated', value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Are monthly check-ins embedded in the program?
            </label>
            <p className="text-xs text-gray-600">
              Regular scheduled meetings between entrepreneurs and their coaches/mentors
            </p>
            <YesPartialNoRadio
              name="monthlyCheckins"
              value={assessmentData.mentorshipCoaching.monthlyCheckins}
              onChange={value => updateField('mentorshipCoaching', 'monthlyCheckins', value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Are reviews embedded in the program?
            </label>
            <p className="text-xs text-gray-600">
              Structured review processes for progress assessment and feedback
            </p>
            <YesPartialNoRadio
              name="reviewsEmbedded"
              value={assessmentData.mentorshipCoaching.reviewsEmbedded}
              onChange={value => updateField('mentorshipCoaching', 'reviewsEmbedded', value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Is there a structured support system?
            </label>
            <p className="text-xs text-gray-600">
              Clear protocols and processes for mentorship and coaching delivery
            </p>
            <YesPartialNoRadio
              name="structuredSupport"
              value={assessmentData.mentorshipCoaching.structuredSupport}
              onChange={value => updateField('mentorshipCoaching', 'structuredSupport', value)}
            />
          </div>

          <div>
            <label
              htmlFor="mentorshipCoachingNotes"
              className="mb-2 block text-sm font-medium text-gray-700"
            >
              Additional Notes
            </label>
            <textarea
              id="mentorshipCoachingNotes"
              value={assessmentData.mentorshipCoaching.notes}
              onChange={e => updateField('mentorshipCoaching', 'notes', e.target.value)}
              rows={3}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-orange-500 focus:ring-orange-500"
              placeholder="Add any additional observations or comments..."
            />
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm"
      >
        <div className="mb-4 flex items-center gap-2">
          <CogIcon className="h-6 w-6 text-indigo-600" />
          <h3 className="text-xl font-semibold text-gray-900">
            Capacity Building for Implementers
          </h3>
        </div>

        <div className="space-y-6">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Are program officers trained using a Train-the-Trainer model?
            </label>
            <p className="text-xs text-gray-600">
              Staff receive comprehensive training to effectively deliver program content
            </p>
            <YesPartialNoRadio
              name="trainTheTrainer"
              value={assessmentData.capacityBuilding.trainTheTrainer}
              onChange={value => updateField('capacityBuilding', 'trainTheTrainer', value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Is there a structure for Officer Coaching?
            </label>
            <p className="text-xs text-gray-600">
              Ongoing coaching and support for program staff to improve their effectiveness
            </p>
            <YesPartialNoRadio
              name="officerCoaching"
              value={assessmentData.capacityBuilding.officerCoaching}
              onChange={value => updateField('capacityBuilding', 'officerCoaching', value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Is there a Performance Review system?
            </label>
            <p className="text-xs text-gray-600">
              Regular evaluation and feedback mechanisms for program staff performance
            </p>
            <YesPartialNoRadio
              name="performanceReview"
              value={assessmentData.capacityBuilding.performanceReview}
              onChange={value => updateField('capacityBuilding', 'performanceReview', value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Is there continuous learning and development?
            </label>
            <p className="text-xs text-gray-600">
              Ongoing professional development opportunities for program staff
            </p>
            <YesPartialNoRadio
              name="continuousLearning"
              value={assessmentData.capacityBuilding.continuousLearning}
              onChange={value => updateField('capacityBuilding', 'continuousLearning', value)}
            />
          </div>

          <div>
            <label
              htmlFor="capacityBuildingNotes"
              className="mb-2 block text-sm font-medium text-gray-700"
            >
              Additional Notes
            </label>
            <textarea
              id="capacityBuildingNotes"
              value={assessmentData.capacityBuilding.notes}
              onChange={e => updateField('capacityBuilding', 'notes', e.target.value)}
              rows={3}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500"
              placeholder="Add any additional observations or comments..."
            />
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm"
      >
        <h3 className="mb-4 text-xl font-semibold text-gray-900">Overall Assessment Notes</h3>
        <textarea
          value={assessmentData.notes}
          onChange={e => setAssessmentData(prev => ({ ...prev, notes: e.target.value }))}
          rows={4}
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:ring-purple-500"
          placeholder="Add any overall observations, recommendations, or strategic insights..."
        />
      </motion.div>
    </div>
  );
};

export default CurriculumDeliveryAssessment;
